import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { AvatarRelease } from "../../ports.ts";
import type { BuilderApi } from "../api.ts";
import { ErrorNote, errText, formatWhen } from "../ui.tsx";

/** Grid of published studio releases. Used to create an employee and to re-pin one. */
export function AvatarPicker({ api, selectedId, onPick, busy }: { api: BuilderApi; selectedId?: string; onPick: (a: AvatarRelease) => void; busy?: boolean }) {
  const [avatars, setAvatars] = useState<AvatarRelease[]>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    api.avatars().then(setAvatars, (e) => setError(`The avatar studio could not be reached: ${errText(e)}`));
  }, [api]);

  if (error) return <ErrorNote error={error} />;
  if (!avatars)
    return (
      <div className="grid gap-3 sm:grid-cols-3">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    );
  if (!avatars.length)
    return <p className="text-sm text-muted-foreground">No avatars are published in the studio yet. Approve and publish one at the avatar studio first.</p>;

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {avatars.map((a) => {
        const selected = a.id === selectedId;
        return (
          <Card key={a.id} className={selected ? "ring-2 ring-primary" : ""}>
            <CardHeader>
              <CardTitle>{a.name}</CardTitle>
              <CardDescription>
                Voice {a.voice}
                {a.created ? ` · published ${formatWhen(a.created)}` : ""}
              </CardDescription>
              <div className="mt-3">
                <Button size="sm" variant={selected ? "secondary" : "default"} disabled={busy || selected} onClick={() => onPick(a)}>
                  {selected ? "Pinned" : "Use this avatar"}
                </Button>
              </div>
            </CardHeader>
          </Card>
        );
      })}
    </div>
  );
}
